import React, { useState, useEffect } from 'react';
import {
  IonButton,
  IonButtons,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonToolbar,
  IonAvatar,
  IonText,
  IonChip,
} from '@ionic/react';
import {
  personOutline,
  logOutOutline,
  searchOutline,
  clipboardOutline,
  analyticsOutline,
  homeOutline,
  menuOutline,
  wifiOutline,
  cloudOfflineOutline,
  warning,
  buildOutline,
} from 'ionicons/icons';
import seeSorLogo from '@/assets/images/SeeSOR.png';
import { useHistory, useLocation } from 'react-router-dom';
import { auth } from '../../services/UserAuthService';
import { networkService } from '../../services/NetworkService';
import { ConnectionStatus } from '@capacitor/network';
import { useOffline } from '../../contexts/OfflineContext';
import { featureFlags } from '../../config/featureFlags';
import { usePageTitleContext } from '@/contexts/PageTitleContext';


interface MenuItem {
  title: string;
  url: string;
  icon: string;
}

const AppHeader: React.FC = () => {
  const { title, contractName } = usePageTitleContext();
  const location = useLocation();
  const isMainPage = location.pathname === '/main';
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNetworkPopoverOpen, setIsNetworkPopoverOpen] = useState(false);
  const [userName, setUserName] = useState<string>('User');
  const [networkStatus, setNetworkStatus] = useState<ConnectionStatus | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const history = useHistory();
  const { isOfflineModeEnabled } = useOffline();

  // Reporting: platform flag AND page flag must allow it AND offline mode must not be active
  const showReporting = featureFlags.isReportingEnabled() && featureFlags.isPageEnabled('reporting') && !isOfflineModeEnabled;
  // Offline Management: platform flag must allow it AND offline mode must be active
  const showOfflineManagement = featureFlags.isOfflineManagementEnabled() && isOfflineModeEnabled;
  const showInspecting = featureFlags.isPageEnabled('inspecting');
  const showPlanning = featureFlags.isPageEnabled('planning');
  const showCorrectiveActions = featureFlags.isPageEnabled('correctiveActions');

  const menuItems: MenuItem[] = isOfflineModeEnabled
    ? [
        { title: 'Main', url: '/main', icon: homeOutline },
        ...(showInspecting ? [{ title: 'Inspecting', url: '/inspecting', icon: searchOutline }] : []),
        ...(showOfflineManagement ? [{ title: 'Offline Ops', url: '/offline-operations', icon: cloudOfflineOutline }] : []),
      ]
    : [
        { title: 'Main', url: '/main', icon: homeOutline },
        ...(showInspecting ? [{ title: 'Inspecting', url: '/inspecting', icon: searchOutline }] : []),
        ...(showCorrectiveActions ? [{ title: 'Corrective Actions', url: '/corrective-actions', icon: buildOutline }] : []),
        ...(showReporting ? [{ title: 'Reporting', url: '/reporting', icon: analyticsOutline }] : []),
        ...(showPlanning ? [{ title: 'Planning', url: '/planning', icon: clipboardOutline }] : []),
        ...(showOfflineManagement ? [{ title: 'Offline Ops', url: '/offline-operations', icon: cloudOfflineOutline }] : []),
      ];

  useEffect(() => {
    // Get user info from sessionStorage
    try {
      const userInfoString = sessionStorage.getItem('user_info');
      if (userInfoString) {
        const userInfo = JSON.parse(userInfoString);
        setUserName(userInfo.username || userInfo.name || 'SeeSOR User');
      }
    } catch (error) {
      console.error('Error parsing user info:', error);
      setUserName('SeeSOR User');
    }

  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Get initial network status
    const currentStatus = networkService.getCurrentStatus();
    setNetworkStatus(currentStatus);

    // Listen to network status changes
    const unsubscribe = networkService.addListener((status: ConnectionStatus) => {
      setNetworkStatus(status);
    });

    // Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  const handleLogout = () => {
    auth.logOut();
    history.push('/login');
  };

  const navigateTo = (url: string) => {
    history.push(url);
    setIsMenuOpen(false);
  };

  const getNetworkIcon = () => {
    if (!networkStatus) return wifiOutline;
    
    if (networkStatus.connected) {
      return wifiOutline;
    } else {
      return cloudOfflineOutline;
    }
  };

  const getNetworkColor = () => {
    if (!networkStatus) return 'medium';
    
    if (networkStatus.connected) {
      return 'success';
    } else {
      return 'danger';
    }
  };

  const getConnectionTypeDisplay = (connectionType: string) => {
    const typeMap: { [key: string]: string } = {
      'wifi': 'WiFi',
      'cellular': 'Cellular',
      'none': 'No Connection',
      'unknown': 'Unknown',
    };
    
    return typeMap[connectionType] || connectionType;
  };

  const renderNetworkIndicator = () => {
    return (
      <>
        <IonButton
          fill="clear"
          size="small"
          id="network-status-trigger"
          onClick={() => setIsNetworkPopoverOpen(true)}
        >
          <IonIcon
            icon={getNetworkIcon()}
            color={getNetworkColor()}
            size="small"
          />
        </IonButton>

        <IonPopover
          trigger="network-status-trigger"
          isOpen={isNetworkPopoverOpen}
          onDidDismiss={() => setIsNetworkPopoverOpen(false)}
          showBackdrop={true}
        >
          <div style={{ padding: '16px', minWidth: '200px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <IonIcon
                icon={getNetworkIcon()}
                color={getNetworkColor()}
                style={{ marginRight: '8px' }}
              />
              <IonText>
                <strong>Network Status</strong>
              </IonText>
            </div>
            
            {networkStatus && (
              <>
                <IonChip
                  color={networkStatus.connected ? 'success' : 'danger'}
                  style={{ marginBottom: '8px' }}
                >
                  <IonLabel>
                    {networkStatus.connected ? 'Online' : 'Offline'}
                  </IonLabel>
                </IonChip>
                
                <div style={{ marginBottom: '8px' }}>
                  <IonText color="medium">
                    <small>Connection Type:</small>
                  </IonText>
                  <br />
                  <IonText>
                    {getConnectionTypeDisplay(networkStatus.connectionType)}
                  </IonText>
                </div>

                {!networkStatus.connected && (
                  <div style={{ marginTop: '12px', padding: '8px', backgroundColor: 'var(--ion-color-warning-tint)', borderRadius: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <IonIcon icon={warning} color="warning" style={{ marginRight: '8px' }} />
                      <IonText color="warning">
                        <small>Working offline</small>
                      </IonText>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </IonPopover>
      </>
    );
  };

  const renderLeftSection = () => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          width: isMobile ? '100%' : 'auto',
          gap: '4px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IonButton
            fill="clear"
            id="menu-trigger"
            onClick={() => setIsMenuOpen(true)}
          >
            <IonIcon icon={menuOutline} />
          </IonButton>
          <IonPopover
            trigger="menu-trigger"
            isOpen={isMenuOpen}
            onDidDismiss={() => setIsMenuOpen(false)}
            showBackdrop={true}
          >
            <IonList>
              {menuItems.map((item, index) => (
                <IonItem
                  key={index}
                  button
                  onClick={() => navigateTo(item.url)}
                >
                  <IonIcon icon={item.icon} slot="start" />
                  <IonLabel>{item.title}</IonLabel>
                </IonItem>
              ))}
            </IonList>
          </IonPopover>
          {!isMainPage && (
            <IonButton fill="clear" onClick={() => navigateTo('/main')}>
              <IonIcon icon={homeOutline} />
            </IonButton>
          )}
        </div>
        {!isMainPage && title && (
          <span
            style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--ion-text-color)',
              marginLeft: isMobile ? '12px' : '4px',
              maxWidth: isMobile ? 'calc(100vw - 24px)' : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
        )}
      </div>
    );
  };

  return (
    <IonHeader>
      <IonToolbar>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
                flexDirection: 'row',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
            }}
          >
            {!isMobile && (
              <IonButtons style={{ flex: '0 0 auto' }}>
                {renderLeftSection()}
              </IonButtons>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', padding: '0.75rem 0', flexWrap: 'wrap' }}>
                <img src={seeSorLogo} alt="SeeSOR logo" style={{ width: '120px', height: 'auto' }} />
              </div>

              {contractName && (
                <div
                  title={contractName}
                  style={{
                    width: '100%',
                    maxWidth: 'min(70vw, 520px)',
                    padding: '0 8px',
                    boxSizing: 'border-box',
                    fontSize: '0.75rem',
                    fontWeight: 400,
                    opacity: 0.85,
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {contractName}
                </div>
              )}
            </div>

            <IonButtons style={{ flex: '0 0 auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {renderNetworkIndicator()}
                <IonAvatar style={{ width: '32px', height: '32px' }}>
                  <IonIcon icon={personOutline} />
                </IonAvatar>
                <IonText style={{ marginRight: '8px', fontSize: '14px' }}>
                  {userName}
                </IonText>
                <IonButton
                  fill="clear"
                  onClick={handleLogout}
                  color="danger"
                >
                  <IonIcon icon={logOutOutline} />
                </IonButton>
              </div>
            </IonButtons>
          </div>

          {isMobile && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                marginTop: '6px',
                padding: '4px 0 2px',
                borderTop: '1px solid var(--ion-color-light-shade)',
                background: 'var(--ion-color-light-tint)',
              }}
            >
              <IonButtons>
                {renderLeftSection()}
              </IonButtons>
            </div>
          )}
        </div>
      </IonToolbar>
    </IonHeader>
  );
};

export default AppHeader;